import { useState, useEffect } from "react";
import { FormattedABRRequest, SummaryDetailsInterface } from "../../../shared/interface/advanceBookingRequest";
import { CustomNotification } from "../../../util/CustomNotification";
import * as advanceBookingRequestService from "../../../services/advanceBookingRequestService";
import useValue from "../../../hooks/useValue";

import type { CheckboxChangeEvent } from "antd/es/checkbox";

interface PaymentSummaryProps {
  data: FormattedABRRequest | null;
  handleRefreshList: () => void;
  isVisible: boolean;
  handleOnClose: () => void;
  loading: boolean;
  summaryData: SummaryDetailsInterface | null;
}

export const usePaymentDetails = ({ data, handleRefreshList, handleOnClose, summaryData }: PaymentSummaryProps) => {
  /* CONSTANT */

  /* STATE  */
  const [totalAmount, setTotalAmount] = useState<number>(0);

  const { value: saving, handleDisableValue: disableSaving, handleEnableValue: enableSaving } = useValue(false);
  const { value: isPenaltyChecked, handleValue: setIsPenaltyChecked } = useValue(false);
  const { value: isValetParkingChecked, handleValue: setIsValetParkingChecked } = useValue(false);

  /* HANDLER */
  const handleCheckboxChange = (e: CheckboxChangeEvent) => {
    if (!summaryData) return;

    const isChecked = e.target.checked;
    const checkBoxName = e.target.name;

    if (checkBoxName === "penalty_amount") {
      if (isChecked) {
        setTotalAmount((prevState) => prevState + summaryData.charge.penalty_amount);
      } else {
        setTotalAmount((prevState) => prevState - summaryData.charge.penalty_amount);
      }
      setIsPenaltyChecked(isChecked);
    } else if (checkBoxName === "valet_parking_amount") {
      if (isChecked) {
        setTotalAmount((prevState) => prevState + summaryData.charge.valet_parking_amount);
      } else {
        setTotalAmount((prevState) => prevState - summaryData.charge.valet_parking_amount);
      }
      setIsValetParkingChecked(isChecked);
    }
  };

  const handleSubmit = async () => {
    if (!data?.advance_booking_request_id) return;

    try {
      enableSaving();

      const response = await advanceBookingRequestService.markAbrRequestCompleteAPI(data.advance_booking_request_id, { is_penalty: isPenaltyChecked, is_valet_parking: isValetParkingChecked, overdue_amount: summaryData?.amount_to_be_paid?.total_overdue_amount ?? 0 });

      CustomNotification.showSuccessMessage(response?.message);
      handleRefreshList();
      handleOnClose();
      setIsPenaltyChecked(false);
      setIsValetParkingChecked(false);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      disableSaving();
    }
  };

  /* EFFECT */
  useEffect(() => {
    if (!summaryData) return;

    let calcTotalAmount = 0;
    calcTotalAmount += summaryData.amount_to_be_paid.parking_amount;
    calcTotalAmount += summaryData.amount_to_be_paid.valet_parking_amount;
    calcTotalAmount += summaryData.amount_to_be_paid.total_overdue_amount;

    if (isPenaltyChecked) calcTotalAmount += summaryData.charge.penalty_amount;
    if (isValetParkingChecked) calcTotalAmount += summaryData.charge.valet_parking_amount;

    setTotalAmount(calcTotalAmount);
  }, [summaryData]);

  return {
    isPenaltyChecked,
    isValetParkingChecked,
    totalAmount,
    handleCheckboxChange,
    saving,
    handleSubmit
  };
};
